import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import _isEmpty from 'lodash/isEmpty'
import { useIntl, FormattedMessage } from 'react-intl'
import { dealerMonitorServices } from '@racepointenergy/swc/scs/dealer-monitor-services'
import {
  serviceSelectors,
  initialState as servicesInitialState,
} from '@racepointenergy/swc/scs/features/host-services'
import {
  createSelectServiceStateFromService,
  initialState as statesInitialState,
} from '@racepointenergy/swc/scs/features/host-states'
import type { ServiceState } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import type { HomeId } from '@racepointenergy/swc/scs/types'
import { Row, SortingRule } from 'react-table'
import _sortBy from 'lodash/sortBy'

import { SpinnerCentered } from 'components/Loader'
import BaseMobileModal from 'components/Modal/BaseMobileModal'
import { getSortOptions } from 'components/Table/utils'
import { CommonSearch, MobileSortBy } from 'components/Table'
import { DropdownOption } from 'components/Input/Dropdown'
import { commonMessages } from 'app/i18n/messages'
import { ModalControlContext } from './modalControlContext'
import { ALL_TYPES_JSON_FILENAME, SERVICES_HEADERS } from './consts'
import { StyledFadeInWrapper } from 'components/Layout/CommonLayout/styles'
import { HomeStateContext } from 'features/home/homeStateContext'

import SysMonGuard, { GuardRouterProps } from '../../SysMonGuard'
import HomeServicesTable from './HomeServicesTable'
import { homeServicesMessages } from './messages'
import HomeServicesEvents from './HomeServicesEvents'
import { getFilterOptions } from './utils'
import { HomeServicesEvent } from './types'
import { homeServicesEventsMessages } from './HomeServicesEvents/messages'
import {
  HomeServicesBody,
  HomeServicesContainer,
  HomeServicesEventsWrapper,
  HomeServicesFilterDropdown,
  HomeServicesFiltersWrapper,
  HomeServicesInfo,
  HomeServicesSearchWrapper,
  HomeServicesTableContainer,
  HomeServicesTitle,
} from './styles'
import { createDropdownOption } from 'features/home/utils'

interface HomeServicesProps {
  homeId: HomeId
}

function HomeServices({ homeId }: HomeServicesProps) {
  const { isOnline } = useContext(HomeStateContext)
  const { formatMessage } = useIntl()
  const {
    data: rawServices = servicesInitialState,
    isError: servicesIsError,
    isFetching: servicesIsFetching,
    error: servicesError,
  } = dealerMonitorServices.useQuery({ id: homeId })

  const {
    data: states = statesInitialState,
    isError: statesIsError,
    isFetching: statesIsFetching,
    error: statesError,
  } = dealerMonitorStates.useQuery({
    id: homeId,
  })

  const [searchValue, setSearchValue] = useState<string>()

  // Status filter state and options
  const [allStatusesDropdownOption] = useState(() =>
    createDropdownOption(
      formatMessage(homeServicesMessages.allStatusesDropdownOption),
      ''
    )
  )
  const [statusesDropdownOptions] = useState(() => [
    allStatusesDropdownOption,
    createDropdownOption(
      formatMessage(homeServicesMessages.activeStatusesDropdownOption),
      true
    ),
    createDropdownOption(
      formatMessage(homeServicesMessages.inactiveStatusesDropdownOption),
      false
    ),
  ])
  const [statusFilter, setStatusFilter] = useState<DropdownOption>(
    allStatusesDropdownOption
  )

  // Zones filter state and options
  const [allZonesDropdownOption] = useState(() =>
    createDropdownOption(
      formatMessage(homeServicesEventsMessages.allZonesDropdownOption),
      ''
    )
  )
  const zonesDropdownOptions = useMemo(
    () =>
      _sortBy(
        getFilterOptions(
          allZonesDropdownOption,
          serviceSelectors.selectAll(rawServices),
          'zone'
        ),
        'label'
      ),
    [allZonesDropdownOption, rawServices]
  )
  const [zoneFilter, setZoneFilter] = useState<DropdownOption | undefined>(
    allZonesDropdownOption
  )

  // Types filter state and options
  const [allTypesDropdownOption] = useState(() =>
    createDropdownOption(
      formatMessage(homeServicesEventsMessages.allTypesDropdownOption),
      ALL_TYPES_JSON_FILENAME
    )
  )

  const [serviceTypeFilter, setServiceTypeFilter] = useState<
    DropdownOption | undefined
  >(allTypesDropdownOption)

  // Services filter state and options
  const [allServicesDropdownOption] = useState(() =>
    createDropdownOption(
      formatMessage(homeServicesEventsMessages.allServicesDropdownOption),
      ''
    )
  )

  const [serviceIdFilter, setServiceIdFilter] = useState<
    DropdownOption | undefined
  >(allServicesDropdownOption)

  const [selectedTableService, setSelectedTableService] = useState<
    HomeServicesEvent | undefined
  >()

  // callback for table on service row selection
  const onSelectService = useCallback(
    (row: Row<ServiceState>) => {
      if (row && selectedTableService?.serviceId !== row.original.id) {
        setSelectedTableService({
          zone: row.original.zone,
          serviceId: row.original.id,
          serviceType: row.original.serviceType,
        })
      }
    },
    [selectedTableService]
  )

  const services = useMemo(() => {
    if (_isEmpty(rawServices?.services?.entities)) {
      return []
    }
    // preparing service data for table
    let services = serviceSelectors
      .selectAll(rawServices)
      .map((service) => {
        return service
          ? createSelectServiceStateFromService(service)(states)
          : null
      })
      .filter((service) => service) as ServiceState[]
    // applying Zone filter
    if (zoneFilter?.value) {
      services = services.filter(
        (service) => zoneFilter.value === service?.zone
      )
    }
    // applying serviceType filter
    if (
      serviceTypeFilter?.value &&
      serviceTypeFilter?.value !== allTypesDropdownOption.value
    ) {
      services = services.filter(
        (service) => serviceTypeFilter.value === service?.serviceType
      )
    }
    // applying serviceId filter
    if (serviceIdFilter?.value) {
      services = services.filter(
        (service) => serviceIdFilter.value === service?.id
      )
    }
    // applying Status(active/inactive) filter
    if (statusFilter.value !== allStatusesDropdownOption.value) {
      services = services.filter(
        (service) => statusFilter.value === service?.isActive
      )
    }

    return services
  }, [
    rawServices,
    zoneFilter?.value,
    serviceTypeFilter?.value,
    serviceIdFilter?.value,
    statusFilter?.value,
    allTypesDropdownOption.value,
    allStatusesDropdownOption.value,
    states,
  ])

  const servicesDropdownOptions = useMemo(
    () => getFilterOptions(allServicesDropdownOption, services, 'id'),
    [allServicesDropdownOption, services]
  )

  const typesDropdownOptions = useMemo(
    () => getFilterOptions(allTypesDropdownOption, services, 'serviceType'),
    [allTypesDropdownOption, services]
  )

  const [headers] = useState(() => ({
    active: {
      label: formatMessage(homeServicesMessages.activeColumn),
      id: SERVICES_HEADERS.IS_ACTIVE,
    },
    component: {
      label: formatMessage(homeServicesMessages.componentColumn),
      id: SERVICES_HEADERS.COMPONENT,
    },
    logicalComponent: {
      label: formatMessage(homeServicesMessages.logicalComponentColumn),
      id: SERVICES_HEADERS.LOGICAL_COMPONENT,
    },
    variant: {
      label: formatMessage(homeServicesMessages.variantColumn),
      id: SERVICES_HEADERS.VARIANT,
    },
    serviceType: {
      label: formatMessage(homeServicesMessages.serviceTypeColumn),
      id: SERVICES_HEADERS.SERVICE_TYPE,
    },
    zone: {
      label: formatMessage(homeServicesMessages.zoneColumn),
      id: SERVICES_HEADERS.ZONE,
    },
  }))
  const [sortOptions] = useState(() => getSortOptions(headers))
  const [sortBy, setSortBy] = useState<SortingRule<ServiceState>>(
    sortOptions[0]
  )

  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false)
  const handleModalClose = () => setIsEventsModalOpen(false)
  const handleModalOpen = () => setIsEventsModalOpen(true)

  const isFetching =
    (servicesIsFetching || statesIsFetching) && _isEmpty(services)
  const hasNoServices =
    !servicesIsFetching && !statesIsFetching && _isEmpty(services)
  const hasServices = !_isEmpty(services)

  useEffect(() => {
    if (servicesIsError) {
      console.log('Error getting home services: ', servicesError)
    }
    if (statesIsError) {
      console.log('Error getting home states for services: ', statesError)
    }
  }, [servicesIsError, servicesError, statesIsError, statesError])

  return (
    <StyledFadeInWrapper>
      <HomeServicesContainer>
        <HomeServicesTitle>
          {formatMessage(homeServicesMessages.title)}
        </HomeServicesTitle>

        <HomeServicesBody>
          {isOnline && (
            <HomeServicesEventsWrapper>
              <HomeServicesEvents
                homeId={homeId}
                selectedTableService={selectedTableService}
                services={services}
                setZoneFilter={setZoneFilter}
                setServiceTypeFilter={setServiceTypeFilter}
                setServiceIdFilter={setServiceIdFilter}
                zonesDropdownOptions={zonesDropdownOptions}
                servicesDropdownOptions={servicesDropdownOptions}
                typesDropdownOptions={typesDropdownOptions}
              />
            </HomeServicesEventsWrapper>
          )}

          {isEventsModalOpen && (
            <BaseMobileModal isOpen>
              <ModalControlContext.Provider
                value={{ handleModalOpen, handleModalClose }}
              >
                <HomeServicesEvents
                  homeId={homeId}
                  selectedTableService={selectedTableService}
                  services={services}
                  setZoneFilter={setZoneFilter}
                  setServiceTypeFilter={setServiceTypeFilter}
                  setServiceIdFilter={setServiceIdFilter}
                  zonesDropdownOptions={zonesDropdownOptions}
                  servicesDropdownOptions={servicesDropdownOptions}
                  typesDropdownOptions={typesDropdownOptions}
                />
              </ModalControlContext.Provider>
            </BaseMobileModal>
          )}

          <HomeServicesTableContainer>
            <HomeServicesFiltersWrapper>
              <HomeServicesFilterDropdown
                name={formatMessage(
                  homeServicesMessages.statusDropdownPlaceholder
                )}
                value={statusFilter}
                onChange={(option: DropdownOption) => setStatusFilter(option)}
                options={statusesDropdownOptions}
                placeholder={formatMessage(
                  homeServicesMessages.statusDropdownPlaceholder
                )}
              />

              <HomeServicesSearchWrapper>
                <CommonSearch
                  setSearchValue={setSearchValue}
                  placeholder={formatMessage(
                    homeServicesMessages.searchPlaceholder
                  )}
                />
              </HomeServicesSearchWrapper>
            </HomeServicesFiltersWrapper>

            <MobileSortBy<ServiceState>
              headers={headers}
              sortOptions={sortOptions}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />

            {statesIsError || servicesError ? (
              <HomeServicesInfo>
                {formatMessage(commonMessages.commonError)}
              </HomeServicesInfo>
            ) : (
              <>
                {isFetching && <SpinnerCentered />}
                {hasNoServices && (
                  <HomeServicesInfo>
                    {formatMessage(homeServicesMessages.noServicesFound)}
                  </HomeServicesInfo>
                )}
                {hasServices && (
                  <ModalControlContext.Provider
                    value={{ handleModalOpen, handleModalClose }}
                  >
                    <HomeServicesTable
                      headers={headers}
                      services={services}
                      defaultSortBy={sortOptions[0]}
                      sortBy={sortBy}
                      setSortBy={setSortBy}
                      searchValue={searchValue}
                      onSelectService={onSelectService}
                    />
                  </ModalControlContext.Provider>
                )}
              </>
            )}
          </HomeServicesTableContainer>
        </HomeServicesBody>
      </HomeServicesContainer>
    </StyledFadeInWrapper>
  )
}

const HomeServicesGuard = (props: GuardRouterProps) => {
  const { homeId } = props.match.params
  return (
    <SysMonGuard
      homeId={homeId}
      title={<FormattedMessage {...homeServicesMessages.title} />}
    >
      <HomeServices homeId={homeId} />
    </SysMonGuard>
  )
}

export default HomeServicesGuard
